/**
 * Route patterns for the page key (pillar B). `templateUrl()` only
 * collapses ids and numbers, so content routes such as `/sv/word/飲む`
 * and `/sv/word/休む` would each be a "page". A project pattern like
 * `/:lang/word/:slug` collapses them. Patterns are user-accepted; the
 * suggestions below never apply themselves.
 */
import { z } from "zod";
import { templateUrl } from "../fingerprint/url-template.js";

const PARAM = /^:[a-z][a-z0-9_]*$/i;

/** `/a/:b/c` — literal segments or `:name`; no query, no trailing slash. */
export const routePatternSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .transform((p) => normalizePattern(p))
  .refine((p) => p !== null, {
    message: "Use a path like /:lang/word/:slug",
  })
  .transform((p) => p as string);

/** A normalised pattern, or null when it isn't a valid path pattern. */
export function normalizePattern(input: string): string | null {
  const raw = input.trim();
  if (!raw.startsWith("/") || /[?#\s]/.test(raw)) return null;
  const segments = raw.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  if (segments.some((s) => s.startsWith(":") && !PARAM.test(s))) return null;
  return `/${segments.join("/")}`;
}

const segmentsOf = (path: string) => path.split("/").filter(Boolean);

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The decoded path of a URL (no origin, query or trailing slash). */
export function pathOfUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    return null;
  }
  const segments = segmentsOf(path).map(decode);
  return `/${segments.join("/")}`;
}

export function matchesPattern(pattern: string, path: string): boolean {
  const p = segmentsOf(pattern);
  const s = segmentsOf(path);
  return (
    p.length === s.length &&
    p.every((seg, i) => (seg.startsWith(":") ? s[i].length > 0 : seg === s[i]))
  );
}

/**
 * Quantifies pattern specificity: more literal (static) segments come first,
 * and among equal literals, fewer parameter segments come first.
 */
export function patternSpecificity(pattern: string): {
  literals: number;
  params: number;
} {
  const segs = segmentsOf(pattern);
  let literals = 0;
  let params = 0;
  for (const seg of segs) {
    if (seg.startsWith(":")) params++;
    else literals++;
  }
  return { literals, params };
}

/**
 * Sorts patterns by specificity:
 * 1. More literal segments first (e.g. `/docs/intro` before `/docs/:slug`)
 * 2. Fewer parameter segments first
 */
export function comparePatternSpecificity(a: string, b: string): number {
  const specA = patternSpecificity(a);
  const specB = patternSpecificity(b);
  if (specB.literals !== specA.literals) return specB.literals - specA.literals;
  if (specA.params !== specB.params) return specA.params - specB.params;
  return 0;
}

/** Route key of a path: the first matching pattern in specificity order, else its template. */
export function routeKeyForPath(
  path: string,
  patterns: readonly string[] = [],
): string {
  const sorted = [...patterns].sort(comparePatternSpecificity);
  return sorted.find((p) => matchesPattern(p, path)) ?? templateUrl(path);
}

export interface RoutePatternSuggestion {
  pattern: string;
  /** Steps the pattern would match. */
  steps: number;
  /** Distinct values of the most varied parameter. */
  distinct: number;
  examples: string[];
}

/** Thresholds: a segment position needs ≥ 5 values across ≥ 3 steps. */
export const SUGGEST_MIN_DISTINCT = 5;
export const SUGGEST_MIN_STEPS = 3;

function paramName(previous: string | undefined, taken: Set<string>) {
  const base =
    previous &&
    !previous.startsWith(":") &&
    /^[a-z][a-z0-9_-]*$/i.test(previous)
      ? previous.replace(/-/g, "_").replace(/s$/, "")
      : "param";
  let name = base;
  for (let i = 2; taken.has(name); i++) name = `${base}${i}`;
  taken.add(name);
  return `:${name}`;
}

/**
 * Patterns worth proposing for a project's step paths (one entry per
 * step, templated or not). Paths with the same number of segments are
 * compared position by position; a position with at least
 * `SUGGEST_MIN_DISTINCT` values becomes a parameter, and a pattern is
 * proposed when at least `SUGGEST_MIN_STEPS` steps match it and every
 * parameter still has that many values. Existing patterns and steps
 * they already match are left out.
 */
export function suggestRoutePatterns(
  paths: readonly string[],
  existing: readonly string[] = [],
): RoutePatternSuggestion[] {
  const open = paths
    .map((p) => templateUrl(p))
    .filter((p) => !existing.some((e) => matchesPattern(e, p)));
  const byLength = new Map<number, string[][]>();
  for (const p of open) {
    const segs = segmentsOf(p);
    if (segs.length === 0) continue;
    byLength.set(segs.length, [...(byLength.get(segs.length) ?? []), segs]);
  }
  const out: RoutePatternSuggestion[] = [];
  for (const [length, group] of byLength) {
    if (group.length < SUGGEST_MIN_STEPS) continue;
    // Single-segment root paths (/pricing, /contact) are discrete pages.
    // Proposing /:param collapses the entire top-level navigation.
    if (length <= 1) continue;

    const varied = Array.from({ length }, (_, i) => i).filter(
      (i) =>
        !group[0][i].startsWith(":") &&
        new Set(group.map((g) => g[i])).size >= SUGGEST_MIN_DISTINCT,
    );
    // A suggested pattern must have at least one static literal anchor segment.
    // Never suggest pure wildcard patterns like /:param1/:param2 where all segments vary.
    if (varied.length === 0 || varied.length >= length) continue;
    const candidates = new Map<string, string[][]>();
    for (const segs of group) {
      const taken = new Set<string>();
      const pattern = `/${segs
        .map((s, i) => (varied.includes(i) ? paramName(segs[i - 1], taken) : s))
        .join("/")}`;
      candidates.set(pattern, [...(candidates.get(pattern) ?? []), segs]);
    }
    for (const [pattern, matched] of candidates) {
      if (matched.length < SUGGEST_MIN_STEPS || existing.includes(pattern)) {
        continue;
      }
      // A suggested pattern must have at least one static literal anchor segment.
      if (!segmentsOf(pattern).some((s) => !s.startsWith(":"))) {
        continue;
      }
      const distinct = Math.min(
        ...varied.map((i) => new Set(matched.map((m) => m[i])).size),
      );
      if (distinct < SUGGEST_MIN_DISTINCT) continue;
      out.push({
        pattern,
        steps: matched.length,
        distinct,
        examples: [...new Set(matched.map((m) => `/${m.join("/")}`))].slice(
          0,
          3,
        ),
      });
    }
  }
  return out.sort(
    (a, b) => b.steps - a.steps || a.pattern.localeCompare(b.pattern),
  );
}
